import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TextField, Tooltip } from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import { Table } from 'antd'
import type { TableColumnsType } from 'antd'
import { trackPromise } from 'react-promise-tracker'
import services from '../../services/index.js'
import { useNotification } from '../../hooks/useNotification.js'

interface LinhaConsumo {
  usuarioId: number
  nome: string
  respostas: number
  semContagem: number
  erros: number
  tokensEntrada: number
  tokensSaida: number
  tokensTotal: number
  limite?: number | null
  ultimoUso?: string | null
}

const inteiro = new Intl.NumberFormat('pt-BR')
const dataHora = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' })

const mesAtual = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

const Dica = ({ texto, children }: { texto: string; children: React.ReactNode }) => (
  <Tooltip title={texto}>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      {children} <InfoOutlined sx={{ fontSize: 14, color: '#6b7a80' }} />
    </span>
  </Tooltip>
)

/** Uso do agente no mês, por pessoa. Inclui quem já não está liberado mas usou no período. */
export default function Consumo({ agenteId }: { agenteId: number }) {
  const { notify: notificar } = useNotification()
  const notificarRef = useRef(notificar)
  notificarRef.current = notificar

  const [mes, setMes] = useState(mesAtual())
  const [linhas, setLinhas] = useState<LinhaConsumo[]>([])
  const [busca, setBusca] = useState('')

  const carregar = useCallback(async (periodo: string) => {
    const [ano, m] = periodo.split('-').map(Number)
    if (!ano || !m) return
    const res = await trackPromise(services.api.oneflowConsumo(agenteId, ano, m))
    if (res.fail) {
      notificarRef.current(res.error || 'Não foi possível consultar o consumo', { variant: 'error' })
      return
    }
    setLinhas(res.data || [])
  }, [agenteId])

  useEffect(() => { carregar(mes) }, [carregar, mes])

  const filtradas = useMemo(() => {
    const t = busca.trim().toLowerCase()
    return t ? linhas.filter(l => l.nome.toLowerCase().includes(t)) : linhas
  }, [linhas, busca])

  const total = useMemo(() => filtradas.reduce((s, l) => ({
    respostas: s.respostas + l.respostas,
    semContagem: s.semContagem + l.semContagem,
    erros: s.erros + l.erros,
    tokensEntrada: s.tokensEntrada + l.tokensEntrada,
    tokensSaida: s.tokensSaida + l.tokensSaida,
    tokensTotal: s.tokensTotal + l.tokensTotal
  }), { respostas: 0, semContagem: 0, erros: 0, tokensEntrada: 0, tokensSaida: 0, tokensTotal: 0 }), [filtradas])

  const numero = (campo: keyof LinhaConsumo, titulo: React.ReactNode) => ({
    title: titulo,
    key: campo,
    align: 'right' as const,
    sorter: (a: LinhaConsumo, b: LinhaConsumo) => (a[campo] as number) - (b[campo] as number),
    render: (_: any, r: LinhaConsumo) => inteiro.format(r[campo] as number)
  })

  const colunas: TableColumnsType<LinhaConsumo> = [
    { title: 'Pessoa', key: 'nome', sorter: (a, b) => a.nome.localeCompare(b.nome), render: (_, r) => r.nome },
    numero('respostas', 'Respostas'),
    numero('semContagem', <Dica texto="Respostas em que o Snowflake não informou o uso. Não entram na soma de tokens.">Sem contagem</Dica>),
    numero('erros', <Dica texto="Perguntas que falharam: tempo esgotado, Snowflake fora do ar, permissão.">Erros</Dica>),
    numero('tokensEntrada', 'Tokens de entrada'),
    numero('tokensSaida', 'Tokens de saída'),
    {
      ...numero('tokensTotal', 'Tokens no mês'),
      defaultSortOrder: 'descend',
      render: (_, r) => {
        const pct = r.limite ? Math.round((r.tokensTotal / r.limite) * 100) : null
        return (
          <span style={{ fontWeight: 'bold' }}>
            {inteiro.format(r.tokensTotal)}
            {pct !== null && <span style={{ fontWeight: 'normal', fontSize: 12, color: pct >= 90 ? '#b45309' : '#6b7a80' }}> ({pct}%)</span>}
          </span>
        )
      }
    },
    { title: 'Limite', key: 'limite', align: 'right', render: (_, r) => (r.limite ? inteiro.format(r.limite) : '—') },
    {
      title: 'Último uso',
      key: 'ultimoUso',
      sorter: (a, b) => (a.ultimoUso || '').localeCompare(b.ultimoUso || ''),
      render: (_, r) => (r.ultimoUso ? dataHora.format(new Date(r.ultimoUso)) : '—')
    }
  ]

  return (
    <>
      <h2>Consumo</h2>
      <p style={{ fontSize: 13, color: '#6b7a80', marginTop: 0 }}>
        Tokens usados por pessoa no mês. Conversas arquivadas com "Nova conversa" continuam contando.
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <TextField size="small" type="month" label="Mês" value={mes} InputLabelProps={{ shrink: true }}
                   inputProps={{ max: mesAtual() }} onChange={e => e.target.value && setMes(e.target.value)} />
        <div style={{ flex: 1 }} />
        <TextField size="small" label="Buscar pessoa" value={busca} onChange={e => setBusca(e.target.value)} />
      </div>

      <Table
        size="small"
        columns={colunas}
        dataSource={filtradas}
        rowKey={r => String(r.usuarioId)}
        pagination={filtradas.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
        scroll={{ x: true }}
        locale={{ emptyText: busca ? 'Ninguém encontrado' : 'Ninguém usou o oneflow deste agente neste mês' }}
        summary={() => filtradas.length > 1 ? (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">{inteiro.format(total.respostas)}</Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right">{inteiro.format(total.semContagem)}</Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="right">{inteiro.format(total.erros)}</Table.Summary.Cell>
            <Table.Summary.Cell index={4} align="right">{inteiro.format(total.tokensEntrada)}</Table.Summary.Cell>
            <Table.Summary.Cell index={5} align="right">{inteiro.format(total.tokensSaida)}</Table.Summary.Cell>
            <Table.Summary.Cell index={6} align="right"><strong>{inteiro.format(total.tokensTotal)}</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={7} />
            <Table.Summary.Cell index={8} />
          </Table.Summary.Row>
        ) : null}
      />
    </>
  )
}
